import { z } from "zod";
import { IndustryTagId, InterestedPropertyTypeId, OccupationTagId } from "./Tag";

const sharedScoreSchema = z.union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5)]);
export type SharedScoreFilter = z.infer<typeof sharedScoreSchema>;
const lengthOfServiceSchema = z.union([z.literal(0.5), z.literal(1), z.literal(3)]);
export type LengthOfServiceFilter = z.infer<typeof lengthOfServiceSchema>;

export const LENGTH_OF_SERVICE_OPTIONS: { value: LengthOfServiceFilter; label: string }[] = [
  { value: 0.5, label: "半年以上" },
  { value: 1, label: "1年以上" },
  { value: 3, label: "3年以上" },
];

// 借入額（年収倍率）の絞り込み選択肢。借入残高 ≦ 年収 × value で絞り込む（value倍以下）。
export const LOAN_AMOUNT_MULTIPLE_VALUES = [4, 6, 8, 10, 12, 14, 16, 18, 20, 25] as const;
export type LoanAmountMultipleFilter = (typeof LOAN_AMOUNT_MULTIPLE_VALUES)[number];
export const LOAN_AMOUNT_MULTIPLE_OPTIONS: { value: LoanAmountMultipleFilter; label: string }[] =
  LOAN_AMOUNT_MULTIPLE_VALUES.map((value) => ({ value, label: `～${value}倍` }));
const loanAmountMultipleSchema = z
  .number()
  .refine((value): value is LoanAmountMultipleFilter =>
    (LOAN_AMOUNT_MULTIPLE_VALUES as readonly number[]).includes(value)
  ) as z.ZodType<LoanAmountMultipleFilter>;

// 0 は「未選択（興味未設定ユーザー）」を表すsentinelのため nonnegative（api_schema の interested_property_type_ids と対応）
const interestTagIdSchema = z.number().int().nonnegative() as z.ZodType<InterestedPropertyTypeId>;

export const userSearchFormSchema = z
  .object({
    userId: z.string().or(z.literal("")),
    score: z.array(sharedScoreSchema).default([]),
    minAge: z.number().or(z.enum(["undefined"])),
    maxAge: z.number().or(z.enum(["undefined"])),
    minIncome: z.number().or(z.enum(["undefined"])),
    maxIncome: z.number().or(z.enum(["undefined"])),
    loanAmountMultiple: loanAmountMultipleSchema.or(z.enum(["undefined"])),
    lengthOfService: lengthOfServiceSchema.or(z.enum(["undefined"])),
    useEstimatedLengthOfService: z.boolean().default(false),
    interestList: z.array(interestTagIdSchema),
    occupationList: z
      .union([
        z.literal(1),
        z.literal(2),
        z.literal(3),
        z.literal(4),
        z.literal(5),
        z.literal(6),
        z.literal(7),
        z.literal(8),
        z.literal(9),
        z.literal(10),
      ])
      .array(),
    industryList: z
      .union([
        z.literal(1),
        z.literal(2),
        z.literal(3),
        z.literal(4),
        z.literal(5),
        z.literal(6),
        z.literal(7),
        z.literal(8),
        z.literal(9),
        z.literal(10),
        z.literal(11),
        z.literal(12),
        z.literal(13),
        z.literal(14),
        z.literal(15),
        z.literal(16),
        z.literal(17),
        z.literal(18),
        z.literal(19),
        z.literal(20),
        z.literal(21),
        z.literal(22),
        z.literal(23),
      ])
      .array(),
    financialAsset: z.number().array().default([]),
    minHoldingPropertyCount: z.number().or(z.enum(["undefined"])),
    maxHoldingPropertyCount: z.number().or(z.enum(["undefined"])),
    days_since_proposal: z.number().array().default([]),
    re_proposal_count: z.number().array().default([]),
    has_other_information: z.boolean().optional(),
    address_prefectures: z.string().array().default([]),
  })
  .refine(
    (args) => {
      const { minAge, maxAge } = args;
      if (minAge === "undefined" || maxAge === "undefined") return true;
      return minAge <= maxAge;
    },
    {
      message: "範囲が逆転しています",
      path: ["minAge"],
    }
  )
  .refine(
    (args) => {
      const { minAge, maxAge } = args;
      if (minAge === "undefined" || maxAge === "undefined") return true;
      return minAge <= maxAge;
    },
    {
      message: "範囲が逆転しています",
      path: ["maxAge"],
    }
  )
  .refine(
    (args) => {
      const { minIncome, maxIncome } = args;
      if (minIncome === "undefined" || maxIncome === "undefined") return true;
      return minIncome <= maxIncome;
    },
    {
      message: "範囲が逆転しています",
      path: ["minIncome"],
    }
  )
  .refine(
    (args) => {
      const { minIncome, maxIncome } = args;
      if (minIncome === "undefined" || maxIncome === "undefined") return true;
      return minIncome <= maxIncome;
    },
    {
      message: "範囲が逆転しています",
      path: ["maxIncome"],
    }
  )
  .refine(
    (args) => {
      const { minHoldingPropertyCount, maxHoldingPropertyCount } = args;
      if (minHoldingPropertyCount === "undefined" || maxHoldingPropertyCount === "undefined") return true;
      return minHoldingPropertyCount <= maxHoldingPropertyCount;
    },
    {
      message: "範囲が逆転しています",
      path: ["minHoldingPropertyCount"],
    }
  )
  .refine(
    (args) => {
      const { minHoldingPropertyCount, maxHoldingPropertyCount } = args;
      if (minHoldingPropertyCount === "undefined" || maxHoldingPropertyCount === "undefined") return true;
      return minHoldingPropertyCount <= maxHoldingPropertyCount;
    },
    {
      message: "範囲が逆転しています",
      path: ["maxHoldingPropertyCount"],
    }
  );
export type UserSearchFormSchema = z.infer<typeof userSearchFormSchema>;

type RangeOption = Partial<{
  min: number | "undefined";
  max: number | "undefined";
}>;

export type UserSearchRequestType = Partial<{
  score: Array<number | null>;
  age: RangeOption;
  annual_income: RangeOption;
  loan_amount_income_multiple: number;
  job_continuous_year: number | "undefined";
  use_estimated_job_continuous_period: boolean;
  interested_property_type_ids: number[];
  job_category_ids: number[];
  job_industry_ids: number[];
  financial_asset: number[];
  holding_property_count: RangeOption;
  days_since_proposal: number[];
  re_proposal_count: number[];
  has_other_information: boolean;
  address_prefectures: string[];
}>;

export type UserSearchEnvelopeRequestType = {
  pagination?: {
    page?: number;
    per_page?: number;
  };
  sort?: string;
  keyword?: string;
  filters?: UserSearchRequestType;
};

export type SearchFormType = {
  userId: string | undefined;
  score: SharedScoreFilter[];
  minAge: number | "undefined";
  maxAge: number | "undefined";
  minIncome: number | "undefined";
  maxIncome: number | "undefined";
  loanAmountMultiple: LoanAmountMultipleFilter | "undefined";
  lengthOfService: LengthOfServiceFilter | "undefined";
  useEstimatedLengthOfService: boolean;
  interestList: InterestedPropertyTypeId[];
  occupationList: OccupationTagId[];
  industryList: IndustryTagId[];
  financialAsset: number[];
  minHoldingPropertyCount: number | "undefined";
  maxHoldingPropertyCount: number | "undefined";
  days_since_proposal: number[];
  re_proposal_count: number[];
  has_other_information?: boolean;
  address_prefectures: string[];
};

export const defaultUserSearchFormType: SearchFormType = {
  userId: "",
  score: [],
  minAge: "undefined",
  maxAge: "undefined",
  minIncome: "undefined",
  maxIncome: "undefined",
  loanAmountMultiple: "undefined",
  lengthOfService: "undefined",
  useEstimatedLengthOfService: false,
  interestList: [],
  occupationList: [],
  industryList: [],
  financialAsset: [],
  minHoldingPropertyCount: "undefined",
  maxHoldingPropertyCount: "undefined",
  days_since_proposal: [],
  re_proposal_count: [],
  has_other_information: false,
  address_prefectures: [],
};
